import type { EditorSetting } from '../entities/editor-setting';
import {
  type EditorSettingSchema,
  type PageWidth,
  pageWidthOptions,
} from '../schema';

export function isPageWidth(value: unknown): value is PageWidth {
  return pageWidthOptions.includes(value as PageWidth);
}

/**
 * The width a doc gets when it has no width of its own. Falls back to the
 * legacy `fullWidthLayout` flag when `defaultPageWidth` was never written.
 */
export function getDefaultPageWidth(
  settings: Pick<EditorSettingSchema, 'defaultPageWidth' | 'fullWidthLayout'>
): PageWidth {
  return (
    settings.defaultPageWidth ??
    (settings.fullWidthLayout ? 'fullWidth' : 'standard')
  );
}

export function resolvePageWidth(
  docPageWidth: string | null | undefined,
  defaultWidth: PageWidth
): PageWidth {
  return isPageWidth(docPageWidth) ? docPageWidth : defaultWidth;
}

/**
 * Keeps `fullWidthLayout` in step so code that only knows the boolean (the
 * peek view, upstream clients) still sees Full width correctly.
 */
export function setDefaultPageWidth(
  editorSetting: EditorSetting,
  width: PageWidth
) {
  editorSetting.set('defaultPageWidth', width);
  editorSetting.set('fullWidthLayout', width === 'fullWidth');
}

const pageWidthAliases: Record<PageWidth, string[]> = {
  standard: ['standard', 'normal'],
  wide: ['wide'],
  fullWidth: ['full width', 'fullwidth', 'full'],
};

/**
 * The widths whose name starts with the typed argument, in display order.
 * An empty argument matches every width.
 */
export function matchPageWidthArgument(argument: string): PageWidth[] {
  const normalized = argument.trim().replace(/\s+/g, ' ').toLowerCase();
  if (!normalized) return [...pageWidthOptions];
  return pageWidthOptions.filter(width =>
    pageWidthAliases[width].some(alias => alias.startsWith(normalized))
  );
}
